import { ScheduleDateType, ScheduledDateCalendarOptions } from '@/dto/scheduledTime';
import { formatDate } from '@/utils/scheduleOrderUtils';
import { DatePicker } from '@clover-web-lib-sprout/date-picker';
import { DateTime } from 'luxon';
import { useTranslations } from 'next-intl';
import React from 'react';
import { DATE_FORMAT_ISO } from '@/utils/constants';

/**
 * Presentational Advanced Ordering date calendar.
 *
 * Owns no scheduling logic: `calendarOptions` and `onDateChange` come from
 * `useScheduledDropdownOptions`, which is the single owner of schedulability and of
 * correcting a selection that is no longer offered. The only thing kept local is the
 * `yyyy-MM-dd` <-> `ScheduleDateType` adaptation the Sprout DatePicker requires.
 */
type Props = {
    /** The currently selected date. */
    scheduleDate: ScheduleDateType;
    /** Boundaries, disabled weekdays and disabled dates, derived from the shared day scan. */
    calendarOptions: ScheduledDateCalendarOptions;
    /** Called with the newly selected date; the hook resets the dependent time selection. */
    onDateChange: (newDate: ScheduleDateType) => void;
    /** The current time zone. */
    timezone: string;
    /** Disabled when no date in range is schedulable. */
    isDisabled?: boolean;
};

export const ScheduledDatePicker = ({
                                        scheduleDate,
                                        calendarOptions,
                                        onDateChange,
                                        timezone,
                                        isDisabled,
                                    }: Props) => {
    const t = useTranslations('ScheduledDatePicker');
    const tPickup = useTranslations('CalculatePickTime');
    const { dateBoundaries, disableDates, disableRecurringDays } = calendarOptions;

    const onDate = (dateStr: string) => {
        const date = DateTime.fromFormat(dateStr, 'yyyy-MM-dd', { zone: timezone }).toJSDate();
        onDateChange({
            displayValue: formatDate(
                DateTime.fromJSDate(date, { zone: timezone }),
                tPickup,
                DateTime.now().setZone(timezone),
            ),
            dateObject: date,
        });
    };

    const formattedValue = React.useMemo(
        () => DateTime.fromJSDate(scheduleDate.dateObject, { zone: timezone }).toFormat(DATE_FORMAT_ISO),
        [scheduleDate, timezone],
    );

    return (
        <DatePicker
            label={t('label')}
            dateBoundaries={dateBoundaries}
            disableDates={disableDates}
            disableRecurringDays={disableRecurringDays}
            enableFutureDates
            disablePastDates
            value={formattedValue}
            onDate={onDate}
            timezone={timezone}
            disabled={isDisabled}
            summaryHour={0}
        />
    );
};
